import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import EditIcon from "@mui/icons-material/Edit";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { Link, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import moment from "moment";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useOutletContext } from "react-router";
import AssetStatus from "../../..//Components/Misc/Status/AssetStatus/asset-status";
import AssetClassChip from "../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import TableCellMovie from "../../../Components/Table/TableCellMovie/table-cell-movie";
import { cacheService, thumbnailService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { GetMetadataByTagName, IsAssetOrderable, IsPermissionGranted } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../Router/router";
import { GetEnumValueLabelMap, GetObjectStatusLabel, UserHasAcl } from "../../../Utils/helpers";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import AssetTypeFromObjectChip from "../../Misc/Chips/AssetChip/asset-type-chip-from-object";
import { ModalContext } from "../../Modals";
import ModalRenameObject, { ModalRenameObjectElementProps } from "../../Modals/ModalRenameObject/modal-rename-object";
import AssetPanel from "../../Panels/AssetPanel/asset-panel";
import DataTable, { CellSize, DataTableProps } from "../table";
import TableCellTypography, { TableCellLongTypography } from "../TableCellTypography/table-cell-typography";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../Redux/redux-store";
import { ResetPanelObject } from "../../../Redux/Reducers/redux-slice-drive";
import TableUuids from "../table-uuids";
import { ObjectClass } from "../../../Core/Api/Types/object-class";

interface AssetTableProps extends Omit<DataTableProps, "columns"> {
  rows: DbAssetObjectInfos[];
  onRemoveRows?: (ids: number[]) => void;
  onNameChanged?: (id: number, name: string) => void;
  onPaginationModelUpdate?: (page: number, pageSize: number) => void;
  parentPermsInherit?: Record<PermissionType, boolean> | null;
  noPanel?: boolean;
  tableSorting?: (GridSortModel) => void;
  paginationModel?: {
    page: number;
    pageSize: number;
  };
  isLoading?: boolean;
}

const AssetTable: FC<AssetTableProps> = (props) => {
  const {
    rows,
    pageSize = 20,
    onRemoveRows,
    onNameChanged,
    onPaginationModelUpdate,
    parentPermsInherit,
    onRowClick,
    paginationModel,
    noPanel = false,
    isLoading = false,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  type Row = any;

  const customerName = cacheService.GetCustomerExternalIdLabel();
  const platformMode = cacheService.GetPlatformMode();

  const enqueueCustomSnackbar = useCustomSnackbar();
  const dispatch = useAppDispatch();

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItems: selectedItems, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
  }>();

  // Modal context
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // Hooks
  const { addObjectByIdToBasket } = useBasketActions();
  const { AddItemsToCollection } = useCollectionActions();
  const apiGrid = useGridApiRef();

  const location = useLocation();

  useEffect(() => {
    if (location.state?.retrieveId !== undefined && apiGrid?.current && pageSize !== undefined && rows) {
      const pageToSet = Math.trunc(rows.findIndex((x) => x.id === location.state?.retrieveId) / pageSize);
      apiGrid?.current?.setPage(pageToSet >= 0 ? pageToSet : 0);
    }
  }, [location.state, apiGrid?.current, rows]);

  // Acls
  const aclOrder = UserHasAcl(AclTypes.aclSectionOrderAccess);

  // Panel
  const [openAssetPanel, setOpenAssetPanel] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(undefined);

  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
    dispatch(ResetPanelObject());
  };

  const showAssetPanel = (e: GridRowParams) => {
    setOpenAssetPanel(true);
    setSelectedAsset(e.row.id);
  };

  const handleSelectionModelChange = (newSelectedRows: GridRowSelectionModel) => {
    const selectedIds = newSelectedRows as number[];
    setSelectedItems((rows ?? []).filter((x) => selectedIds.includes(x.id)));
  };

  const handleRename = (obj) => {
    if (!openModal) return;

    openModal<ModalRenameObjectElementProps>(ModalRenameObject, {
      nameInitial: obj.name,
      objectId: obj.id,
      onConfirm: (name: string) => {
        onNameChanged && onNameChanged(obj.id, name);
      },
    });
  };

  // Cols def
  const cols: GridColDef<DbAssetObjectInfos>[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        minWidth: CellSize.xsmall,
        flex: CellSize.xsmall,
        renderCell: (data: Row) => (
          <Link
            color="primary"
            underline="hover"
            component={RouterLink}
            target="_blank"
            to={`${platformMode === PlatformMode.Distribution ? routePaths.asset.path : routePaths.drive.path}/${
              data?.row?.id
            }`}
          >
            {data.value}
          </Link>
        ),
      },
      {
        field: "parentTitleId",
        headerName: t("common:title"),
        hideable: true,
        minWidth: CellSize.large,
        flex: CellSize.large,
        sortable: false,
        valueGetter: (data: Row, row: any) => `${row?.parentTitleName + " " || ""}#${row?.parentTitleId}`,
        valueFormatter: (params: any, row: any) => {
          if (params) {
            return `${row.parentTitleName + " " || ""}#${row.parentTitleId}`;
          } else return "";
        },
        renderCell: (data: Row) => (
          <TableCellMovie
            id={data?.row?.parentTitleId || data?.row?.id}
            name={data.row["parentTitleName"] || "Drive"}
            isDrive={data?.row?.parentTitleId === null}
          />
        ),
      },
      {
        field: "name",
        headerName: t("common:name"),
        minWidth: CellSize.xlarge,
        flex: CellSize.xlarge,
        renderCell: (data: Row) => <TableCellLongTypography data={data.value} variant="body1" />,
      },
      {
        field: "thumbnail",
        headerName: t("common:thumbnail"),
        hideable: true,
        minWidth: CellSize.small,
        flex: CellSize.small,
        sortable: false,
        filterable: false,
        renderCell: (data: Row) =>
          data?.row?.type === ObjectType.Folder || data?.row?.type === ObjectType.Group ? (
            <FolderOutlinedIcon />
          ) : (
            <img
              src={thumbnailService.GetThumbRefByObjectId(data?.row?.id, cacheService.GetAuthKey()!)}
              alt={`poster ${data?.row?.id}`}
              width={40}
              height={30}
              loading="lazy"
            />
          ),
      },
      {
        field: "titleCustomerId",
        headerName: customerName + " Id",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: any) => (
          <Typography component="span" fontWeight={500} color="text.primary">
            {data.value}
          </Typography>
        ),
      },
      {
        field: "type",
        headerName: t("common:type"),
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectType),
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data) => <AssetTypeFromObjectChip size="small" obj={data.row} />,
      },
      {
        field: "classification",
        headerName: t("common:class"),
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectClass),
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data) => <AssetClassChip size="small" label={data.value} classification={data.value} />,
      },
      {
        field: "creationInfos.date",
        headerName: t("common:upperCaseCreation"),
        type: "date",
        minWidth: CellSize.small,
        flex: CellSize.small,
        valueGetter: (data, row) => row.creationInfos?.date ?? null,
        valueFormatter: (value, row) => {
          if (value) return moment(value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS);
          else return "";
        },
        renderCell: (data) => (
          <TableCellTypography
            data={moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
            color="text.secondary"
          />
        ),
      },
      {
        field: "status",
        headerName: t("common:status"),
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectStatus, GetObjectStatusLabel),
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        renderCell: (data) =>
          data.row.type !== ObjectType.Folder ? (
            <AssetStatus small status={data.value} disabled={GetMetadataByTagName(data.row, "isDisabled")?.value} />
          ) : (
            ""
          ),
      },
      {
        field: "durationSMPTE",
        headerName: t("common:duration"),
        minWidth: CellSize.medium,
        isMeta: true,
        flex: CellSize.medium,
        valueGetter: (data, row) => {
          if (!row) return;
          if (row.type === ObjectType.Video || row?.type === ObjectType.Audio) {
            return (
              (row?.metadata?.find(
                (r) => r.id === cacheService.GetMetadata().find((x) => x.tagName === "durationSMPTE")?.id
              ) &&
                row?.metadata?.find(
                  (r) => r.id === cacheService.GetMetadata().find((x) => x.tagName === "durationSMPTE")?.id
                )?.value) ||
              "Unknown"
            );
          }
          return "";
        },
        renderCell: (data) => {
          return <TableCellTypography color="text.secondary" data={data.value} />;
        },
      },
      {
        field: "actions",
        type: "actions",
        align: "right",
        minWidth: CellSize.actionsXsmall,
        flex: CellSize.actionsXsmall,
        getActions: (params: GridRowParams) => {
          if (!params || !params.row) return;
          let actions: any[] = [];

          if (IsPermissionGranted(params.row, PermissionType.MetadataEdit))
            actions.push(
              <GridActionsCellItem
                icon={<EditIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("common:rename")}
                onClick={() => handleRename(params.row)}
                showInMenu
              />
            );

          if (!params?.row?.trash && IsAssetOrderable(params.row)) {
            actions.push(
              <GridActionsCellItem
                icon={<BookmarkBorderIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("button:addToCollection_one")}
                onClick={() => AddItemsToCollection([params.row])}
                showInMenu
              />,
              <GridActionsCellItem
                icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("common:addToBasket")}
                onClick={() => addObjectByIdToBasket(params?.row?.id)}
                disabled={!aclOrder}
                showInMenu
              />
            );
          }

          return actions;
        },
      },
    ],
    [rows]
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.ASSETS_TABLE}
        rows={rows}
        columns={cols}
        rowHeight={56}
        getRowHeight={() => "auto"}
        checkboxSelection
        pageSize={pageSize}
        disableRowSelectionOnClick
        onRowSelectionModelChange={handleSelectionModelChange}
        paginationModel={paginationModel}
        paginationServerSideChangeCallback={(page, pageSize) =>
          onPaginationModelUpdate && onPaginationModelUpdate(page, pageSize)
        }
        pagination={true}
        onRowClick={(params, event, detail) =>
          onRowClick ? onRowClick(params, event, detail) : showAssetPanel(params)
        }
        paginationMode="server"
        densityControl
        selectedRows={selectedItems.map((x) => x.id)}
        initialColumnVisibility={{
          thumbnail: platformMode === PlatformMode.Browser,
          titleCustomerId: customerName !== "" && platformMode === PlatformMode.Distribution,
          status: platformMode === PlatformMode.Distribution,
          classification: platformMode === PlatformMode.Distribution,
          parentTitleId: platformMode === PlatformMode.Distribution,
          id: true,
        }}
        apiRef={apiGrid}
        {...otherProps}
      />
      {!noPanel && (
        <AssetPanel
          open={openAssetPanel}
          onClose={hideAssetPanel}
          closePanel={hideAssetPanel}
          assetId={selectedAsset}
          reload={selectedAsset}
        />
      )}
    </>
  );
};

export default AssetTable;
